import { ChangeEvent, useEffect, useState } from 'react'

import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Spacer,
  Spinner,
  SystemProps,
  Text,
} from '@chakra-ui/react'
import {
  faPause,
  faMicrophone,
  faArrowUpFromBracket,
  faArrowDownToBracket,
} from '@fortawesome/pro-solid-svg-icons'
import { Duration } from 'luxon'

import AudioPlayerInline from 'src/components/widgets/AudioPlayerInline/AudioPlayerInline'
import Icon from 'src/components/widgets/Icon/Icon'
import { AssetType } from 'src/lib/assets/assetsTypes'
import { useCurrentUser } from 'src/lib/hooks/auth/useCurrentUser'
import { UseFieldProps } from 'src/lib/hooks/form/useField'
import { itListTheme } from 'src/theme'
import { BrandColor } from 'src/types'

import ErrorMessage from '../ErrorMessage/ErrorMessage'
import UploadTrigger from '../UploadTrigger/UploadTrigger'
import VoiceTipIcon from '../VoiceTipIcon/VoiceTipIcon'

import MicrophoneInstructionModal from './MicrophoneInstructionModal/MicrophoneInstructionModal'
import useRecorder, { Recorder, UseRecorder } from './useRecorder'
import BrandedVoiceTipIcon from '../BrandedVoiceTipIcon/BrandedVoiceTipIcon'
import { faPauseCircle } from '@fortawesome/pro-regular-svg-icons'

type RecordProps = {
  startRecording: () => void
  recordingFailed: boolean
  setModalOpen: (isOpen: boolean) => void
}

const ONE_MINUTE_IN_SEC = 60

const record = ({
  startRecording,
  recordingFailed,
  setModalOpen,
}: RecordProps) => {
  if (recordingFailed) {
    setModalOpen(true)
  } else {
    startRecording()
  }
}

const SendAudioControls = ({
  startRecording,
  recordingFailed,
  setModalOpen,
  onSelectFile,
}: {
  onSelectFile: (e: ChangeEvent<HTMLInputElement>) => Promise<void>
} & RecordProps) => {
  return (
    <>
      <Box position="relative" width="44px" height="44px">
        <IconButton
          variant="function"
          borderRadius="full"
          width="44px"
          height="44px"
          aria-label="Send audio button"
          icon={<Icon icon={faArrowUpFromBracket} />}
        ></IconButton>
        <UploadTrigger
          accept="audio/*"
          onChangeFile={onSelectFile}
          testid="voiceTipRecorder-uploadAudio"
        />
      </Box>
      <Box
        position="relative"
        width="44px"
        height="44px"
        data-testid="voiceTipRecorder-recordAudio"
      >
        <IconButton
          variant="function"
          width="44px"
          height="44px"
          onClick={() =>
            record({ startRecording, recordingFailed, setModalOpen })
          }
          aria-label="Start recording button"
          icon={<Icon icon={faMicrophone} />}
        />
      </Box>
    </>
  )
}

const RecordingContainer = ({
  brandColor,
  recordingSeconds,
  isPaused,
}: {
  brandColor: BrandColor
  recordingSeconds: number
  isPaused: boolean
}) => {
  return (
    <Flex alignItems="center" gap={2} h="21px">
      {isPaused && (
        <>
          <Icon icon={faPauseCircle} color="stone" />
          <Text className="body-extra-small" color="stone">
            Recording paused
          </Text>
        </>
      )}
      {!isPaused && (
        <>
          <Spinner size="md" color={brandColor} />
          <Text className="body-extra-small" color="stone">
            Recording...
          </Text>
        </>
      )}
      <Spacer />
      <Text className="body-extra-small" color="stone">
        {formatTime(recordingSeconds)}
      </Text>
    </Flex>
  )
}

const formatTime = (sec: number) => {
  const duration = Duration.fromMillis(sec * 1000)
  if (sec < ONE_MINUTE_IN_SEC) {
    return duration.toFormat("s's'")
  } else {
    return duration.toFormat('mm:ss')
  }
}

const SavingContainer = ({ brandColor }: { brandColor: BrandColor }) => {
  return (
    <Flex alignItems="flex-end" gap={2}>
      <Spinner size="md" color={brandColor} />
      <Text className="body-extra-small" color="stone">
        Saving...
      </Text>
    </Flex>
  )
}

export type VoiceTipRecorderProps = {
  title: string
  subtitle?: string
  name: string
  // For the download we need to use the final s3 url
  // If we just recorded the audio, it has not be converted
  // to a downloadable url yet, and the resulting blob
  // in `audio` is not yet downloadable
  finalSrc: string
  audio?: string
  onClear?: () => void
  type: AssetType
  id: number
  showProfileImage?: boolean
} & SystemProps & { fieldOptions?: UseFieldProps<Recorder['audio']> }

const VoiceTipRecorder = ({
  type,
  id,
  name,
  finalSrc,
  subtitle,
  title,
  showProfileImage = false,
  onClear,
  ...props
}: VoiceTipRecorderProps) => {
  const { brandColor, profileImage } = useCurrentUser()
  const {
    recorderState: { isRecording, recordFailed, recordingSeconds, isPaused },
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    field,
    audio,
    selectFileHandler,
    clearRecording,
    isSaving,
  }: UseRecorder = useRecorder({
    audio: props?.audio,
    name,
    type,
    id,
    onClear,
  })

  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (recordFailed) {
      setModalOpen(true)
    }
  }, [recordFailed])

  return (
    <>
      <Flex
        transition="box-shadow 0.2s ease-in-out"
        direction="column"
        borderRadius="12px"
        border="1px solid"
        borderColor="sandstonePlus"
        padding={4}
        gap={4}
        boxShadow={isRecording ? itListTheme.shadows.sm : '0 0 0 rgba(1,1,1,0)'}
        data-coachmark="editor-5"
        {...props}
      >
        <Flex gap={3} alignItems="center">
          {showProfileImage && (
            <VoiceTipIcon brandColor={brandColor} profileImage={profileImage} />
          )}
          {!showProfileImage && (
            <BrandedVoiceTipIcon
              brandColor={brandColor}
              iconOutlineColor="white"
              w="24px"
              h="24px"
            />
          )}

          <Flex flexDirection="column">
            <Box className="body-small-emphasis" color="onyx">
              {title}
            </Box>
            <Box className="input-label" color="stone">
              {field?.error && (
                <ErrorMessage mb={2} message={field.error.message} />
              )}
              {(!field?.error && subtitle) || ''}
            </Box>
          </Flex>
          <Spacer />
          {isRecording && isPaused && (
            <IconButton
              variant="function"
              borderRadius="full"
              width="36px"
              height="36px"
              onClick={() => resumeRecording()}
              aria-label="Resume recording"
              icon={<Icon icon={faMicrophone} />}
            ></IconButton>
          )}
          {isRecording && !isPaused && (
            <IconButton
              variant="function"
              borderRadius="full"
              width="36px"
              height="36px"
              onClick={() => pauseRecording()}
              aria-label="Pause recording"
              icon={<Icon icon={faPause} />}
            ></IconButton>
          )}
          {(isRecording || isSaving) && (
            <Button
              variant="function"
              size="sm"
              onClick={() => stopRecording()}
              isDisabled={isSaving}
              data-testid="voiceTipRecorder-doneButton"
            >
              Done
            </Button>
          )}

          {field.value && (
            <Flex align="center" gap={3}>
              {!!finalSrc && (
                <IconButton
                  as="a"
                  variant="function"
                  borderRadius="full"
                  width="36px"
                  height="36px"
                  href={finalSrc}
                  download={true}
                  aria-label="Download recording"
                  icon={<Icon icon={faArrowDownToBracket} />}
                ></IconButton>
              )}
              <Button
                variant="function"
                size="sm"
                onClick={() => clearRecording()}
                data-testid="voiceTipRecorder-redoButton"
              >
                Redo
              </Button>
            </Flex>
          )}

          {!isSaving && !isRecording && !field.value && (
            <SendAudioControls
              startRecording={startRecording}
              recordingFailed={recordFailed}
              setModalOpen={setModalOpen}
              onSelectFile={selectFileHandler}
            />
          )}
        </Flex>
        {(isRecording || isSaving || field.value) && (
          <>
            <Divider />
            {isRecording && (
              <RecordingContainer
                isPaused={isPaused}
                brandColor={brandColor}
                recordingSeconds={recordingSeconds}
              />
            )}
            {isSaving && <SavingContainer brandColor={brandColor} />}
            {field.value && (
              <AudioPlayerInline
                mediaUrl={audio}
                title={title}
                pictureUrl={profileImage}
                brandColor={brandColor}
                showProfileImage={showProfileImage}
              />
            )}
          </>
        )}
      </Flex>
      <MicrophoneInstructionModal
        brandColor={brandColor}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
      />
    </>
  )
}

export default VoiceTipRecorder
